import { useDispatch, useSelector } from "react-redux";

import { ROUTES } from "../configs/routes";
import {
  EVENT_CATEGORY_HEADER,
  EVENT_HEADER_ACTION_LOGIN,
  EVENT_HEADER_ACTION_LOGOUT,
} from "../constants/analytics";
import { API_BASE_URL, API_ENDPOINTS, API_SA_BASE_URL } from "../constants/api";
import { CLIENT_ID, DL_CLIENT_ID } from "../constants/appConstants";
import { GEOIP_CHINA_COUNTRY_CODE } from "../constants/locale";
import config from "../constants/serverConfig";
import {
  AUTH_TYPE,
  COOKIE_GST_ATKN,
  COOKIE_SP_ATKN,
  EMAIL_LOGIN_VERIFICATION_RESULT,
  REDIRECT,
  REDIRECT_URI,
  RESTRICTED,
  SHOW_TIMEOUT_DIALOG,
  USER_DATA_NAMESPACE,
  USER_SESSION_EXPIRED_TIME,
  USER_SESSION_ID,
} from "../constants/storage";
import { URLS } from "../constants/urls";
import { Cookie, LocalStorage } from "../services/storage";
import { ApplicationState } from "../store";
import { resetLanguage } from "../store/actions/layout-actions";
import { actionLogout } from "../store/actions/user-actions";
import {
  actionResetAuth,
  actionSetAuthLoggedIn,
  actionSetAuthType,
} from "../store/actions/auth-actions";
import { LayoutState } from "../store/reducers/layout-reducer";
import { UserState } from "../store/reducers/user-reducer";
import {
  AuthType,
  ConfirmPassEntity,
  DetectAuthResult,
  LoginParams,
  SignInEntity,
} from "../types/auth";
import { ServiceEntity } from "../types/services";
import { REQUEST_TYPE } from "../types/user";
import GoogleAnalytics from "../utils/GoogleAnalytics";
import { getDomain } from "../utils/UtilsBrowser";
import { UtilsRequest } from "../utils/UtilsRequest";
import { generateToken } from "../utils/UtilsTokenGenerator";

declare global {
  interface Window {
    samsung: {
      account: {
        signIn: (locale: SignInEntity, configs: { locale: string }) => void;
      };
    };
  }
}

export const useAuthProvider = () => {
  const dispatch = useDispatch();
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const authState = useSelector<
    ApplicationState,
    { type: AuthType; isLoggedIn: boolean }
  >((state) => state.auth);
  // current domain as params for cookie removal
  const domain = getDomain(window.location.hostname);

  const removeSpAtknCookie = () => {
    Cookie.remove(COOKIE_SP_ATKN, {
      path: "/",
      domain: domain,
    });
  };

  const removeGstAtknCookie = () => {
    Cookie.remove(COOKIE_GST_ATKN, {
      path: "/",
      domain: domain,
    });
  };

  /**
   * Detect auth type from localStorage AUTH_TYPE → cookies/localStorage → null.
   * Returns { type, isLoggedIn } and dispatches to Redux.
   */
  const detectAuthType = (): DetectAuthResult => {
    // 1. Check AUTH_TYPE in localStorage first (canonical signal, persisted by redux-persist)
    const storedAuthType = LocalStorage.get(AUTH_TYPE) as AuthType;

    if (storedAuthType === "EMAIL") {
      // Verify email login is still valid (both localStorage flag and cookie must exist)
      const emailLoginVerificationResult = LocalStorage.get(
        EMAIL_LOGIN_VERIFICATION_RESULT
      );
      const isEmailOTPverified =
        emailLoginVerificationResult && emailLoginVerificationResult === "true";
      const hasGstAtkn = !!Cookie.get(COOKIE_GST_ATKN);

      if (isEmailOTPverified && hasGstAtkn) {
        const result = { type: "EMAIL" as AuthType, isLoggedIn: true };
        dispatch(actionSetAuthType("EMAIL"));
        dispatch(actionSetAuthLoggedIn(true));
        return result;
      }
    } else if (storedAuthType === "SA" || storedAuthType === "DL") {
      // Verify SA/DL login is still valid (SP_ATKN cookie must exist)
      if (Cookie.get(COOKIE_SP_ATKN)) {
        const result = { type: storedAuthType, isLoggedIn: true };
        dispatch(actionSetAuthType(storedAuthType));
        dispatch(actionSetAuthLoggedIn(true));
        return result;
      }
    }

    // 2. Fallback: inspect cookies/localStorage directly (for backward compat with existing sessions)
    const emailLoginVerificationResult = LocalStorage.get(
      EMAIL_LOGIN_VERIFICATION_RESULT
    );
    const isEmailOTPverified =
      emailLoginVerificationResult && emailLoginVerificationResult === "true";
    const isEmailLogin = isEmailOTPverified && Cookie.get(COOKIE_GST_ATKN);
    const isSALogin = Cookie.get(COOKIE_SP_ATKN);

    if (isEmailLogin) {
      const result = { type: "EMAIL" as AuthType, isLoggedIn: true };
      dispatch(actionSetAuthType("EMAIL"));
      dispatch(actionSetAuthLoggedIn(true));
      return result;
    }

    if (isSALogin) {
      // Before profile fetch, we can't distinguish SA from DL.
      // Use stored AUTH_TYPE if available, default to "SA".
      const type = storedAuthType === "DL" ? "DL" : "SA";
      const result = { type: type as AuthType, isLoggedIn: true };
      dispatch(actionSetAuthType(type));
      dispatch(actionSetAuthLoggedIn(true));
      return result;
    }

    // Not logged in
    const result = { type: null as AuthType, isLoggedIn: false };
    dispatch(actionSetAuthType(null));
    dispatch(actionSetAuthLoggedIn(false));
    return result;
  };

  /**
   * Returns current auth.isLoggedIn from Redux selector.
   */
  const validateSession = (): boolean => {
    return authState.isLoggedIn;
  };

  /**
   * Unified login method with type-aware logic.
   * For "SA": uses CLIENT_ID, calls window.samsung.account.signIn.
   * For "DL": uses DL_CLIENT_ID, calls window.samsung.account.signIn.
   * For "EMAIL": no-op (email login handled by OTP flow).
   */
  const login = (type: AuthType, params?: LoginParams) => {
    if (type === "EMAIL") {
      // Email login is handled by the OTP flow, no-op here
      return;
    }

    const redirectURL = params?.redirectURL ?? "";
    const isDigitalLegacy = type === "DL" || params?.isDigitalLegacy === true;

    // cleanup the persisted data in redux to ensure that the profile, and GEOIP API will be called after successfull login
    // note: this will just reset the redux state to it's default value
    dispatch(actionLogout());
    dispatch(actionResetAuth());

    // Set AUTH_TYPE in localStorage as the canonical auth type signal
    LocalStorage.set(AUTH_TYPE, type);

    const appState = generateToken(32);
    const APP_ID = isDigitalLegacy ? DL_CLIENT_ID : CLIENT_ID;
    const authUrl = `${API_BASE_URL}${API_ENDPOINTS.AUTH_SIGNIN_URL}?returnUrl=${API_BASE_URL}${redirectURL}?success=1&appId=${APP_ID}`;

    LocalStorage.remove(USER_SESSION_EXPIRED_TIME);
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_HEADER,
      EVENT_HEADER_ACTION_LOGIN
    );

    const paramsEntity: SignInEntity = {
      responseType: "code",
      clientId: APP_ID,
      redirectUri: encodeURIComponent(authUrl),
      state: appState,
      scope: "",
    };

    window.samsung.account.signIn(
      { ...paramsEntity },
      { locale: `${layoutState.languageCode}-${layoutState.countryCode}` }
    );
  };

  /**
   * Type-specific logout.
   * SA/DL: clears SP_ATKN + USER_SESSION_ID.
   * Email: clears GST_ATKN + EMAIL_LOGIN_VERIFICATION_RESULT.
   * DL logout redirects to China localsite if userCountryCode === 'CHN'.
   * Dispatches actionResetAuth().
   */
  const logout = async (isIdle: boolean = false) => {
    const currentAuthType = authState.type;

    dispatch(resetLanguage());
    dispatch(actionLogout());
    dispatch(actionResetAuth());

    // Type-specific cleanup
    if (currentAuthType === "EMAIL") {
      // Email login cleanup
      removeGstAtknCookie();
      LocalStorage.remove(EMAIL_LOGIN_VERIFICATION_RESULT);
    } else {
      // SA/DL login cleanup
      removeSpAtknCookie();
      LocalStorage.remove(USER_SESSION_ID);
    }

    // Common cleanup
    sessionStorage.removeItem(RESTRICTED);
    sessionStorage.removeItem(REDIRECT);
    Cookie.remove(REDIRECT_URI);
    LocalStorage.remove(USER_DATA_NAMESPACE);
    LocalStorage.remove(USER_SESSION_EXPIRED_TIME);
    LocalStorage.remove(AUTH_TYPE);

    // GA tracking for logout action
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_HEADER,
      EVENT_HEADER_ACTION_LOGOUT
    );

    // For Digital Legacy Users that has GEOIP of CN, They should be redirected to CN Localsites upon logout as they don't have access to privacy site
    if (
      currentAuthType === "DL" &&
      userCountryCode === GEOIP_CHINA_COUNTRY_CODE
    ) {
      window.location.replace(URLS.CHINA_LOCALSITE);
      return;
    }

    if (isIdle) {
      window.history.replaceState(null, "", ROUTES.ROOT);
      localStorage.setItem(SHOW_TIMEOUT_DIALOG, "true");
      return; // return as we dont need to refresh the page when user is automatically logged out, For Idle Modal Dialog to be displayed.
    }

    // Redirect to homepage
    window.location.replace(ROUTES.ROOT);
  };

  const confirmPassword = (
    services: Array<ServiceEntity>,
    requestType: REQUEST_TYPE
  ) => {
    const arrServices: string[] = [];

    services.forEach((service) => {
      arrServices.push(service.service_code);
    });

    UtilsRequest.setRequestInfo(arrServices, requestType);
    const appState = generateToken(32);

    const redirectConf = `${API_BASE_URL}${ROUTES.MYDATA_HISTORY}`;
    const goBackUrl = `${config.APP_BASE_URL_NO_PARAMS}${ROUTES.MYDATA}`;
    const paramsEntity: ConfirmPassEntity = {
      locale: layoutState.languageCode,
      countryCode: layoutState.countryCode,
      redirect_uri: redirectConf,
      state: appState,
      goBackURL: goBackUrl,
    };

    const params = new URLSearchParams({ ...paramsEntity });

    window.location.replace(
      `${API_SA_BASE_URL}${API_ENDPOINTS.CONFIRM_PASS_URL}?${params}`
    );
  };

  return {
    login,
    logout,
    confirmPassword,
    detectAuthType,
    validateSession,
  };
};
